/*
 OK FBI — Suspect Interrogation Room (full-screen, portal)
 Host controls when each question / answer / reaction is revealed for the audience.
 Never exposes guilt — reactions (calm / nervous / defiant / contradiction) are
 dramatic cues only. Character continuity: uses the exact dossier portrait.
*/
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { assetUrl } from "@/contexts/SessionContext";
import { sfx, isSoundEnabled, setSoundEnabled } from "@/lib/sounds";
import * as TTS from "@/lib/tts";
import { X, ChevronRight, ChevronLeft, RotateCcw, Eye, EyeOff, MessageCircle, AlertTriangle, Radio, Play, Pause, Volume2, VolumeX, Mic, MicOff, Clock, SkipForward, Waves } from "lucide-react";

const REACTIONS = {
  calm:          { label: "هادئ",          color: "text-emerald-400", bg: "bg-emerald-950/40", border: "border-emerald-800/70", dot: "bg-emerald-400" },
  nervous:       { label: "متوتر",          color: "text-amber-400",   bg: "bg-amber-950/40",   border: "border-amber-800/70",   dot: "bg-amber-400 animate-pulse" },
  defiant:       { label: "متحدٍّ",         color: "text-red-400",     bg: "bg-red-950/40",     border: "border-red-800/70",     dot: "bg-red-400" },
  contradiction: { label: "تناقض واضح",     color: "text-fuchsia-400", bg: "bg-fuchsia-950/40", border: "border-fuchsia-800/70", dot: "bg-fuchsia-400 animate-pulse" },
};

export function InterrogationRoom({ open, onClose, caseId, suspect }) {
  const [questions, setQuestions] = useState([]);
  const [revealedIdx, setRevealedIdx] = useState([]);   // list of q indices with question shown
  const [currentIdx, setCurrentIdx] = useState(0);
  const [showAnswer, setShowAnswer] = useState({});     // per q: bool
  const [showReaction, setShowReaction] = useState({}); // per q: bool
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!caseId || !suspect?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/interrogation/${caseId}/${suspect.id}`);
      setQuestions(data.questions || []);
      setRevealedIdx(data.revealed_indices || []);
      setCurrentIdx(0);
    } catch { /* 401/403 — interrogation not accessible */ } finally { setLoading(false); }
  }, [caseId, suspect?.id]);

  useEffect(() => { if (open) { load(); sfx.fileOpen(); } else { TTS.stop(); } }, [open, load]);

  if (!open || !suspect) return null;

  const revealQuestion = async (idx) => {
    const wasRevealed = revealedIdx.includes(idx);
    const newVal = !wasRevealed;
    // optimistic
    setRevealedIdx(list => newVal ? [...list, idx] : list.filter(i => i !== idx));
    if (newVal) sfx.evidenceReveal(); else sfx.evidenceUndo();
    try {
      const { data } = await api.post("/session/interrogation/reveal", { case_id: caseId, suspect_id: suspect.id, q_index: idx, revealed: newVal });
      if (data?.revealed_indices) setRevealedIdx(data.revealed_indices);
    } catch (e) {
      // rollback on failure
      setRevealedIdx(list => wasRevealed ? [...list, idx] : list.filter(i => i !== idx));
    }
  };

  const revealAll = async () => {
    try {
      for (let i = 0; i < questions.length; i++) {
        if (!revealedIdx.includes(i)) {
          await api.post("/session/interrogation/reveal", { case_id: caseId, suspect_id: suspect.id, q_index: i, revealed: true });
        }
      }
      sfx.evidenceReveal();
      load();
    } catch {}
  };

  const resetAll = async () => {
    try {
      await api.post(`/session/interrogation/reset/${caseId}/${suspect.id}`);
      setShowAnswer({}); setShowReaction({});
      sfx.evidenceUndo();
      load();
    } catch {}
  };

  const current = questions[currentIdx];
  const isRevealed = revealedIdx.includes(currentIdx);
  const reaction = current ? REACTIONS[current.reaction] || REACTIONS.calm : REACTIONS.calm;

  return createPortal(
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        dir="rtl" data-testid="interrogation-room"
        className="fixed inset-0 z-[999] bg-black text-white p-4 md:p-6 flex flex-col overflow-auto">
        {/* HEADER */}
        <div className="flex items-center justify-between mb-3 shrink-0">
          <div className="flex items-center gap-3">
            <MessageCircle size={24} className="text-gold" strokeWidth={1.2} />
            <div>
              <div className="text-[10px] tracking-[0.4em] text-gold font-mono-ar">INTERROGATION ROOM</div>
              <div className="font-cairo text-2xl">غرفة الاستجواب</div>
            </div>
            <div className="mr-4 flex items-center gap-2 text-[10px] font-mono-ar text-red-400 bg-red-950/40 border border-red-900/60 px-2 py-1">
              <Radio size={11} className="animate-pulse" /> LIVE · RECORDING
            </div>
          </div>
          <button onClick={onClose} data-testid="interrogation-close" className="w-10 h-10 border border-metal hover:border-gold text-zinc-400 hover:text-gold flex items-center justify-center"><X size={18} /></button>
        </div>

        <div className="flex-1 grid grid-cols-1 xl:grid-cols-[280px_1fr_300px] gap-4 min-h-0">
          {/* LEFT: Suspect Card with Reaction Scene */}
          <div className="panel border-gold p-3 flex flex-col" data-testid="interrogation-suspect-card">
            <ReactionScene
              suspect={suspect}
              reaction={showReaction[currentIdx] ? (current?.reaction || null) : null}
              autoPlayKey={showReaction[currentIdx] ? `${currentIdx}` : null}
            />
            <div className="mt-3">
              <div className="text-[10px] tracking-[0.3em] text-gold font-mono-ar">FILE {suspect.id}</div>
              <div className="font-cairo text-2xl text-white mt-1">{suspect.name}</div>
              <div className="text-xs text-zinc-400 mt-1">{suspect.job}{suspect.age ? ` · ${suspect.age} سنة` : ""}</div>
              <div className="mt-3 space-y-1 text-[11px] text-zinc-300 border-t border-metal pt-2">
                <div><span className="text-zinc-500">صلة القرابة:</span> {suspect.relation}</div>
                <div><span className="text-zinc-500">الدافع المحتمل:</span> {suspect.motive}</div>
                {suspect.alibi && <div><span className="text-zinc-500">حجة الغياب:</span> {suspect.alibi.claim} <span className={suspect.alibi.status === "unverified" ? "text-amber-400" : "text-emerald-400"}>({suspect.alibi.status === "unverified" ? "غير مؤكدة" : "مؤكدة"})</span></div>}
              </div>
            </div>
            {/* Voice Fingerprint (host-only) */}
            <VoiceFingerprintPanel suspect={suspect} />
            <div className="mt-auto pt-3 grid grid-cols-2 gap-2">
              <button onClick={revealAll} data-testid="interrogation-reveal-all" className="btn-primary text-[11px] py-1.5">كشف الكل</button>
              <button onClick={resetAll} data-testid="interrogation-reset" className="btn-ghost text-[11px] py-1.5 border-red-900/60 text-red-400 hover:bg-red-950/30">إعادة</button>
            </div>
          </div>

          {/* CENTER: Question + Reveal */}
          <div className="panel border-metal p-6 flex flex-col min-h-0">
            {loading ? (
              <div className="flex-1 flex items-center justify-center text-zinc-500 text-sm">جاري تحميل الاستجواب...</div>
            ) : questions.length === 0 ? (
              <div className="flex-1 flex items-center justify-center text-zinc-500 text-sm">لا توجد أسئلة استجواب لهذا المشتبه به.</div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <div className="text-[10px] tracking-[0.4em] text-gold font-mono-ar">QUESTION {String(currentIdx + 1).padStart(2, "0")} / {String(questions.length).padStart(2, "0")}</div>
                  <div className="flex gap-1">
                    <button onClick={() => setCurrentIdx(i => Math.max(0, i - 1))} data-testid="interrogation-prev" className="w-8 h-8 border border-metal hover:border-gold text-zinc-400 hover:text-gold flex items-center justify-center"><ChevronRight size={14} /></button>
                    <button onClick={() => setCurrentIdx(i => Math.min(questions.length - 1, i + 1))} data-testid="interrogation-next" className="w-8 h-8 border border-metal hover:border-gold text-zinc-400 hover:text-gold flex items-center justify-center"><ChevronLeft size={14} /></button>
                  </div>
                </div>

                {/* Cinematic Interrogation Timer */}
                <InterrogationTimerBar caseId={caseId} suspectId={suspect.id} qIndex={currentIdx} />

                {/* Question bubble */}
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <div className="text-[9px] tracking-widest text-zinc-500 font-mono-ar">المحقق</div>
                      <VoiceNotReadyChip caseId={caseId} suspectId={suspect.id} qIndex={currentIdx} kind="q" />
                    </div>
                    <VoiceControls
                      text={isRevealed ? current.q : ""}
                      tag={`q-${currentIdx}`}
                      testidPrefix={`voice-q-${currentIdx}`}
                    />
                  </div>
                  <div className="border-r-4 border-gold pr-4 py-2">
                    <div className={`font-cairo text-2xl md:text-3xl leading-relaxed text-white transition-opacity ${isRevealed ? "opacity-100" : "opacity-30"}`}>
                      {isRevealed ? current.q : "•••••••••••••••••••••••••••••••••"}
                    </div>
                  </div>
                </div>

                {/* Reveal question button */}
                {!isRevealed && (
                  <button onClick={() => revealQuestion(currentIdx)} data-testid={`interrogation-reveal-q-${currentIdx}`}
                    className="btn-primary w-full py-3 mb-4">كشف السؤال للمشاهدين</button>
                )}
                {isRevealed && (
                  <button onClick={() => revealQuestion(currentIdx)} data-testid={`interrogation-hide-q-${currentIdx}`}
                    className="btn-ghost w-full py-2 mb-4 text-[11px] border-red-900/60 text-red-400 hover:bg-red-950/30">إخفاء السؤال</button>
                )}

                {/* Answer */}
                {isRevealed && (
                  <div className="mb-3">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <div className="text-[9px] tracking-widest text-zinc-500 font-mono-ar">إجابة المشتبه به</div>
                        <VoiceNotReadyChip caseId={caseId} suspectId={suspect.id} qIndex={currentIdx} kind="a" />
                      </div>
                      {showAnswer[currentIdx] && (
                        <AnswerVoiceControls
                          suspect={suspect}
                          text={current.a}
                          tag={`a-${currentIdx}`}
                          testidPrefix={`voice-a-${currentIdx}`}
                        />
                      )}
                    </div>
                    {showAnswer[currentIdx] ? (
                      <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }}
                        className="border-l-4 border-zinc-500 pr-4 pl-4 py-3 bg-zinc-950/50 rounded-sm">
                        <div className="font-cairo text-lg text-zinc-100">{current.a}</div>
                      </motion.div>
                    ) : (
                      <button onClick={() => { setShowAnswer(s => ({ ...s, [currentIdx]: true })); sfx.notification(); }}
                        data-testid={`interrogation-reveal-a-${currentIdx}`}
                        className="btn-ghost w-full py-2 text-xs">▸ كشف الإجابة</button>
                    )}
                  </div>
                )}

                {/* Reaction */}
                {isRevealed && showAnswer[currentIdx] && (
                  <div className="mb-3">
                    <div className="text-[9px] tracking-widest text-zinc-500 font-mono-ar mb-1">قراءة رد الفعل</div>
                    {showReaction[currentIdx] ? (
                      <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }}
                        className={`border ${reaction.border} ${reaction.bg} rounded-sm p-3 flex items-center justify-between`}
                        data-testid={`interrogation-reaction-${currentIdx}`}>
                        <div className="flex items-center gap-2">
                          <span className={`w-2.5 h-2.5 rounded-full ${reaction.dot}`}></span>
                          <span className={`font-cairo text-lg ${reaction.color}`}>{reaction.label}</span>
                          {current.reaction === "contradiction" && <AlertTriangle size={14} className="text-fuchsia-400" />}
                        </div>
                        <span className="text-[10px] font-mono-ar text-zinc-500">BEHAVIORAL READ</span>
                      </motion.div>
                    ) : (
                      <button onClick={() => { setShowReaction(s => ({ ...s, [currentIdx]: true })); sfx.cctvBeep(); }}
                        data-testid={`interrogation-reveal-r-${currentIdx}`}
                        className="btn-ghost w-full py-2 text-xs">▸ كشف رد الفعل</button>
                    )}
                  </div>
                )}

                {/* Linked evidence */}
                {isRevealed && showAnswer[currentIdx] && current.linked?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    <span className="text-[10px] text-zinc-500 font-mono-ar">أدلة مرتبطة:</span>
                    {current.linked.map(eid => (
                      <span key={eid} className="evidence-tag">{eid}</span>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* RIGHT: Question list */}
          <div className="panel border-metal flex flex-col min-h-0">
            <div className="px-3 py-2 border-b border-metal text-[10px] tracking-widest text-gold font-mono-ar">قائمة الأسئلة</div>
            <div className="flex-1 overflow-y-auto p-2 space-y-1">
              {questions.map((q, i) => {
                const revealed = revealedIdx.includes(i);
                const isCurrent = i === currentIdx;
                return (
                  <button key={i} onClick={() => setCurrentIdx(i)}
                    data-testid={`interrogation-q-item-${i}`}
                    className={`w-full text-right border p-2 text-xs transition-colors ${isCurrent ? "border-gold bg-[rgba(197,160,89,0.1)]" : "border-metal hover:border-gold/50"}`}>
                    <div className="flex justify-between items-center mb-1">
                      <span className={`text-[9px] font-mono-ar ${revealed ? "text-emerald-400" : "text-zinc-500"}`}>{revealed ? "تم الكشف" : "مخفي"}</span>
                      <span className="text-[10px] font-mono-ar text-gold">Q{String(i + 1).padStart(2, "0")}</span>
                    </div>
                    <div className={`font-cairo text-xs truncate ${revealed ? "text-white" : "text-zinc-500"}`}>
                      {revealed ? q.q : "•••••••••••••••••••"}
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="px-3 py-2 border-t border-metal text-[9px] font-mono-ar text-zinc-500 text-center">
              {revealedIdx.length} / {questions.length} REVEALED
            </div>
          </div>
        </div>
        <div className="text-center pt-2 text-[9px] text-zinc-600 tracking-[0.3em] font-cairo">OK FBI · INTERROGATION · BY OK</div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}

/**
 * ReactionScene — canvas-based cinematic reaction animation for a suspect.
 * Uses the exact dossier portrait (character continuity). No AI generation per frame.
 * Applies subtle procedural transformations based on reaction: breathing, micro-tremor,
 * chin lift, brightness pulse, cold sweat overlay. Optimized for OBS (30fps loop, single canvas).
 */
function ReactionScene({ suspect, reaction, autoPlayKey }) {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(!isSoundEnabled());
  const startRef = useRef(performance.now());
  const rafRef = useRef(null);

  // Load portrait
  useEffect(() => {
    if (!suspect?.portrait) return;
    const img = new Image();
    img.onload = () => { imgRef.current = img; setLoaded(true); };
    img.onerror = () => { imgRef.current = null; setLoaded(false); };
    img.src = assetUrl(suspect.portrait);
    return () => { imgRef.current = null; setLoaded(false); };
  }, [suspect?.portrait]);

  // Reset timer when a new reaction plays
  useEffect(() => {
    if (autoPlayKey !== null) { startRef.current = performance.now(); setPlaying(true); }
  }, [autoPlayKey, reaction]);

  const replay = () => { startRef.current = performance.now(); setPlaying(true); };
  const toggleMute = () => { const n = !muted; setMuted(n); setSoundEnabled(!n); };

  useEffect(() => {
    const cvs = canvasRef.current; if (!cvs) return;
    const ctx = cvs.getContext("2d");
    const draw = () => {
      const W = cvs.width, H = cvs.height;
      ctx.clearRect(0, 0, W, H);
      // Base scenery — dark grade
      const g = ctx.createLinearGradient(0, 0, 0, H);
      g.addColorStop(0, "#0A0B10"); g.addColorStop(1, "#020204");
      ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
      const img = imgRef.current;
      if (!img) { requestNext(); return; }
      const t = (performance.now() - startRef.current) / 1000; // seconds since play
      // Base transform per reaction
      let dx = 0, dy = 0, scale = 1, tilt = 0, brightness = 1, contrast = 1, hueShift = 0, redEye = 0, sweat = 0;
      if (reaction === "calm") {
        dy = Math.sin(t * 2.4) * 1.5; // breathing
        scale = 1 + Math.sin(t * 2.4) * 0.006;
      } else if (reaction === "nervous") {
        dx = (Math.sin(t * 32) + Math.sin(t * 21)) * 0.6; // micro tremor
        dy = Math.sin(t * 3.6) * 1.4;
        sweat = Math.min(1, Math.max(0, (t - 1.2)) * 0.6);
        brightness = 0.98;
      } else if (reaction === "defiant") {
        dy = -Math.min(4, t * 3); // chin lift up
        scale = 1 + Math.min(0.03, t * 0.02);
        brightness = 1.08; contrast = 1.06;
        tilt = -0.02;
      } else if (reaction === "contradiction") {
        dx = (Math.sin(t * 18) + Math.sin(t * 12)) * 0.9;
        redEye = Math.min(0.55, t * 0.35);
        brightness = 0.94;
        hueShift = 6;
      }
      // Portrait aspect fit 4:5
      const targetW = W * 0.9, targetH = targetW * 1.28;
      const px = (W - targetW) / 2 + dx;
      const py = (H - targetH) / 2 + dy;
      ctx.save();
      ctx.translate(W / 2, H / 2);
      ctx.rotate(tilt);
      ctx.scale(scale, scale);
      ctx.translate(-W / 2, -H / 2);
      ctx.filter = `brightness(${brightness}) contrast(${contrast})`;
      ctx.drawImage(img, px, py, targetW, targetH);
      ctx.restore();

      // Red eye tint overlay for contradiction
      if (redEye > 0) {
        ctx.save();
        ctx.globalAlpha = redEye * 0.28;
        const eyeGradient = ctx.createRadialGradient(W * 0.5, H * 0.36, 5, W * 0.5, H * 0.36, W * 0.35);
        eyeGradient.addColorStop(0, "#7A0F0F"); eyeGradient.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = eyeGradient;
        ctx.fillRect(0, 0, W, H);
        ctx.restore();
      }
      // Sweat drop
      if (sweat > 0.2) {
        const dropY = H * 0.36 + (sweat - 0.2) * 120;
        ctx.save();
        ctx.globalAlpha = Math.min(1, sweat);
        ctx.fillStyle = "rgba(190,220,255,0.75)";
        ctx.beginPath();
        ctx.ellipse(W * 0.58, dropY, 2, 4, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
      // Scanlines + faint vignette (surveillance feel)
      ctx.globalAlpha = 0.06;
      for (let y = 0; y < H; y += 3) {
        ctx.fillStyle = y % 6 === 0 ? "#000" : "rgba(180,220,180,0.3)";
        ctx.fillRect(0, y, W, 1);
      }
      ctx.globalAlpha = 1;
      // vignette
      const vg = ctx.createRadialGradient(W/2, H/2, W*0.25, W/2, H/2, W*0.75);
      vg.addColorStop(0, "rgba(0,0,0,0)"); vg.addColorStop(1, "rgba(0,0,0,0.6)");
      ctx.fillStyle = vg; ctx.fillRect(0, 0, W, H);
      // HUD chrome
      ctx.fillStyle = "rgba(0,0,0,0.7)";
      ctx.strokeStyle = "rgba(220,50,50,0.9)"; ctx.lineWidth = 1;
      ctx.fillRect(W - 78, 8, 70, 18); ctx.strokeRect(W - 78, 8, 70, 18);
      ctx.fillStyle = "#ff5a5a"; ctx.font = "10px monospace";
      ctx.fillText("● REC", W - 68, 21);
      ctx.fillStyle = "rgba(0,0,0,0.7)"; ctx.strokeStyle = "rgba(0,255,220,0.5)";
      ctx.fillRect(8, 8, 210, 18); ctx.strokeRect(8, 8, 210, 18);
      ctx.fillStyle = "#5df3ea"; ctx.fillText(`CAM-INT · ${suspect.id}`, 14, 21);
      ctx.fillStyle = "rgba(0,0,0,0.75)"; ctx.strokeStyle = "rgba(197,160,89,0.6)";
      ctx.fillRect(8, H - 26, 200, 18); ctx.strokeRect(8, H - 26, 200, 18);
      ctx.fillStyle = "#C5A059"; ctx.fillText(`REACTION: ${(reaction || "IDLE").toUpperCase()}`, 14, H - 13);

      requestNext();
    };
    function requestNext() {
      if (!playing) { rafRef.current = null; return; }
      rafRef.current = requestAnimationFrame(draw);
    }
    draw();
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [loaded, reaction, playing, suspect?.id]);

  return (
    <div className="relative" data-testid="interrogation-reaction-scene">
      <canvas ref={canvasRef} width={520} height={650} className="w-full aspect-[4/5] object-cover border border-metal bg-black" />
      <div className="absolute bottom-2 left-2 right-2 flex justify-between items-center pointer-events-auto">
        <div className="flex gap-1">
          <button onClick={() => setPlaying(p => !p)} data-testid="reaction-playpause" title={playing ? "إيقاف" : "تشغيل"}
            className="w-8 h-8 border border-gold/60 text-gold bg-black/70 hover:bg-[rgba(197,160,89,0.15)] flex items-center justify-center">
            {playing ? <Pause size={12} /> : <Play size={12} />}
          </button>
          <button onClick={replay} data-testid="reaction-replay" title="إعادة تشغيل"
            className="w-8 h-8 border border-metal text-zinc-300 bg-black/70 hover:border-gold hover:text-gold flex items-center justify-center">
            <RotateCcw size={12} />
          </button>
          <button onClick={toggleMute} data-testid="reaction-mute" title={muted ? "تفعيل الصوت" : "كتم الصوت"}
            className="w-8 h-8 border border-metal text-zinc-300 bg-black/70 hover:border-gold hover:text-gold flex items-center justify-center">
            {muted ? <VolumeX size={12} /> : <Volume2 size={12} />}
          </button>
        </div>
        <div className="text-[9px] font-mono-ar text-gold bg-black/70 border border-gold/40 px-2 py-1">
          CAM-INT · {suspect.id}
        </div>
      </div>
    </div>
  );
}

function CtlBtnSmall({ children, onClick, active, testid, tip, disabled }) {
  return (
    <button onClick={onClick} data-testid={testid} title={tip} disabled={disabled}
      className={`w-8 h-8 flex items-center justify-center border transition-colors ${disabled ? "border-metal/50 text-zinc-700 cursor-not-allowed" : active ? "border-gold text-gold bg-[rgba(197,160,89,0.15)]" : "border-metal text-zinc-300 hover:border-gold/60 hover:text-gold"}`}>
      {children}
    </button>
  );
}

function VoiceControls({ text, tag, testidPrefix }) {
  const [prefs, setPrefs] = useState(TTS.getPrefs());
  const [status, setStatus] = useState(TTS.getStatus());
  const [expanded, setExpanded] = useState(false);
  useEffect(() => TTS.onStateChange(setStatus), []);
  const isMine = status.tag === tag;
  const speaking = isMine && status.status === "speaking";
  const paused = isMine && status.status === "paused";
  const supported = typeof window !== "undefined" && "speechSynthesis" in window;

  const play = () => { if (paused) TTS.resume(); else TTS.speak(text, tag); };
  const doPause = () => { TTS.pause(); };
  const replay = () => { TTS.speak(text, tag); };
  const toggleEnabled = () => { const n = !prefs.enabled; TTS.setEnabled(n); setPrefs(TTS.getPrefs()); };

  return (
    <div className="flex items-center gap-1 relative" data-testid={testidPrefix}>
      {!supported && <span className="text-[9px] text-zinc-500 font-mono-ar">TTS غير متاح</span>}
      {supported && (<>
        {!speaking ? (
          <CtlBtnSmall onClick={play} testid={`${testidPrefix}-play`} tip="تشغيل السؤال صوتياً" disabled={!text || !prefs.enabled}>
            <Play size={12} />
          </CtlBtnSmall>
        ) : (
          <CtlBtnSmall onClick={doPause} testid={`${testidPrefix}-pause`} tip="إيقاف مؤقت"><Pause size={12} /></CtlBtnSmall>
        )}
        <CtlBtnSmall onClick={replay} testid={`${testidPrefix}-replay`} tip="إعادة التشغيل" disabled={!text || !prefs.enabled}>
          <RotateCcw size={11} />
        </CtlBtnSmall>
        <CtlBtnSmall onClick={toggleEnabled} testid={`${testidPrefix}-master`}
          tip={prefs.enabled ? "صوت المحقق مفعل" : "صوت المحقق مكتوم"} active={prefs.enabled}>
          {prefs.enabled ? <Volume2 size={12} /> : <VolumeX size={12} />}
        </CtlBtnSmall>
        <button onClick={() => setExpanded(x => !x)} data-testid={`${testidPrefix}-more`}
          className="text-[9px] font-mono-ar text-zinc-500 hover:text-gold px-1 flex items-center gap-0.5">
          <Mic size={9} /> صوت المحقق
        </button>
        {expanded && (
          <div className="absolute top-9 right-0 bg-black/95 border border-metal rounded-sm p-3 w-64 text-[11px] z-50" data-testid={`${testidPrefix}-panel`}>
            <div className="flex justify-between items-center mb-2">
              <span className="text-gold font-cairo">إعدادات الصوت</span>
              <button onClick={() => setExpanded(false)} className="text-zinc-500 hover:text-white"><X size={11} /></button>
            </div>
            <label className="block mb-2">
              <div className="text-[9px] text-zinc-500 font-mono-ar mb-0.5">السرعة: {prefs.rate.toFixed(2)}×</div>
              <input type="range" min="0.6" max="1.2" step="0.05" value={prefs.rate}
                onChange={(e) => { TTS.setRate(e.target.value); setPrefs(TTS.getPrefs()); }}
                className="w-full accent-yellow-600" data-testid={`${testidPrefix}-rate`} />
            </label>
            <label className="block mb-2">
              <div className="text-[9px] text-zinc-500 font-mono-ar mb-0.5">الحدّة: {prefs.pitch.toFixed(2)}</div>
              <input type="range" min="0.5" max="1.2" step="0.05" value={prefs.pitch}
                onChange={(e) => { TTS.setPitch(e.target.value); setPrefs(TTS.getPrefs()); }}
                className="w-full accent-yellow-600" data-testid={`${testidPrefix}-pitch`} />
            </label>
            <label className="block mb-2">
              <div className="text-[9px] text-zinc-500 font-mono-ar mb-0.5">الحجم: {Math.round(prefs.volume * 100)}%</div>
              <input type="range" min="0" max="1" step="0.01" value={prefs.volume}
                onChange={(e) => { TTS.setVolume(e.target.value); setPrefs(TTS.getPrefs()); }}
                className="w-full accent-yellow-600" data-testid={`${testidPrefix}-vol`} />
            </label>
            <div className="text-[9px] text-zinc-500 font-mono-ar mb-1">اختيار الصوت</div>
            <select value={prefs.voiceURI || ""} onChange={(e) => { TTS.setVoice(e.target.value || null); setPrefs(TTS.getPrefs()); }}
              data-testid={`${testidPrefix}-voice`}
              className="w-full bg-black/40 border border-metal px-2 py-1 text-[11px] text-white outline-none focus:border-gold">
              <option value="">— تلقائي (يفضل الذكوري العربي) —</option>
              {TTS.listVoices().map(v => <option key={v.voiceURI} value={v.voiceURI}>{v.name} · {v.lang}</option>)}
            </select>
            <div className="text-[9px] text-zinc-600 mt-1 leading-tight">صوت المحقق عربي عميق يعتمد على أصوات النظام. للحصول على أفضل جودة استخدم Chrome أو Edge.</div>
          </div>
        )}
      </>)}
    </div>
  );
}

function AnswerVoiceControls({ suspect, text, tag, testidPrefix }) {
  const [status, setStatus] = useState(TTS.getStatus());
  const [prefs, setPrefs] = useState(TTS.getPrefs());
  useEffect(() => TTS.onStateChange(setStatus), []);
  const isMine = status.tag === tag;
  const speaking = isMine && status.status === "speaking";
  const paused = isMine && status.status === "paused";
  const supported = typeof window !== "undefined" && "speechSynthesis" in window;
  const play = () => { if (paused) TTS.resume(); else TTS.speakAs(suspect, text, tag); };
  const doPause = () => { TTS.pause(); };
  const replay = () => { TTS.speakAs(suspect, text, tag); };
  const toggleMaster = () => { const n = !prefs.answersEnabled; TTS.setAnswersEnabled(n); setPrefs(TTS.getPrefs()); };
  const changeVol = (v) => { TTS.setAnswersVolume(v); setPrefs(TTS.getPrefs()); };
  const profile = supported ? TTS.voiceProfileFor(suspect) : null;
  if (!supported) return null;
  return (
    <div className="flex items-center gap-1" data-testid={testidPrefix}>
      {!speaking
        ? <CtlBtnSmall onClick={play} testid={`${testidPrefix}-play`} tip="تشغيل الإجابة صوتياً" disabled={!text || !prefs.answersEnabled}><Play size={12} /></CtlBtnSmall>
        : <CtlBtnSmall onClick={doPause} testid={`${testidPrefix}-pause`} tip="إيقاف مؤقت"><Pause size={12} /></CtlBtnSmall>}
      <CtlBtnSmall onClick={replay} testid={`${testidPrefix}-replay`} tip="إعادة التشغيل" disabled={!text || !prefs.answersEnabled}><RotateCcw size={11} /></CtlBtnSmall>
      <CtlBtnSmall onClick={toggleMaster} testid={`${testidPrefix}-master`} tip={prefs.answersEnabled ? "أصوات المتهمين مفعلة" : "أصوات المتهمين مكتومة"} active={prefs.answersEnabled}>
        {prefs.answersEnabled ? <Volume2 size={12} /> : <VolumeX size={12} />}
      </CtlBtnSmall>
      <input type="range" min="0" max="1" step="0.05" value={prefs.answersVolume}
        onChange={(e) => changeVol(e.target.value)} data-testid={`${testidPrefix}-vol`}
        title={`صوت المتهمين: ${Math.round(prefs.answersVolume * 100)}%`}
        className="w-16 accent-yellow-600" />
      {profile && <span className="text-[8px] text-zinc-500 font-mono-ar ml-1" title={`Voice: ${profile.voice?.name || "auto"} · ${profile.gender}`}>
        {profile.gender === "female" ? "♀" : "♂"} · pitch {profile.pitch.toFixed(2)}
      </span>}
    </div>
  );
}


// ============ Interrogation Timer Bar ============
// Per-question cinematic countdown, synced to backend + Overlay via /overlay/state.
// Host-controlled: Start / Pause / Resume / Reset / Skip. Duration selectable (15 / 30 / 60 / 90 s).
function InterrogationTimerBar({ caseId, suspectId, qIndex }) {
  const [timer, setTimer] = useState(null);       // {seconds_left, duration, running, q_index, started_at}
  const [duration, setDuration] = useState(30);
  const [now, setNow] = useState(Date.now());

  // Poll session every 1.5s for authoritative timer state
  useEffect(() => {
    let stop = false;
    const load = async () => {
      try {
        const { data } = await api.get("/session");
        if (stop) return;
        const t = data?.interrogation_timer;
        // Only adopt timer when it matches this suspect + question
        if (t && t.case_id === caseId && t.suspect_id === suspectId && t.q_index === qIndex) {
          setTimer(t);
        } else if (t && (t.case_id !== caseId || t.suspect_id !== suspectId || t.q_index !== qIndex)) {
          setTimer(null);
        } else if (!t) {
          setTimer(null);
        }
      } catch {}
    };
    load();
    const iv = setInterval(load, 1500);
    const nowIv = setInterval(() => setNow(Date.now()), 250);
    return () => { stop = true; clearInterval(iv); clearInterval(nowIv); };
  }, [caseId, suspectId, qIndex]);

  // Local smoothing for the running timer (server ticks every 1.5s → smooth locally)
  const smoothed = useMemo(() => {
    if (!timer) return null;
    if (!timer.running || !timer.started_at) return timer.seconds_left ?? 0;
    try {
      const start = new Date(timer.started_at).getTime();
      const elapsed = Math.max(0, (now - start) / 1000);
      return Math.max(0, Math.round((timer.seconds_left ?? 0) - elapsed));
    } catch { return timer.seconds_left ?? 0; }
  }, [timer, now]);

  const call = async (action) => {
    try {
      const { data } = await api.post("/session/interrogation-timer", {
        case_id: caseId, suspect_id: suspectId, q_index: qIndex, action,
        duration: action === "start" ? duration : undefined,
      });
      if (data?.timer) setTimer(data.timer);
      // Feedback stinger
      if (action === "start") sfx.cctvBeep?.();
      if (action === "skip")  sfx.evidenceUndo?.();
    } catch {}
  };

  const active = timer && timer.suspect_id === suspectId && timer.q_index === qIndex;
  const remaining = active ? (smoothed ?? 0) : 0;
  const dur = active ? (timer.duration || duration) : duration;
  const pct = active && dur > 0 ? Math.max(0, Math.min(100, (remaining / dur) * 100)) : 0;
  const running = !!(active && timer?.running);
  const expired = active && !running && remaining === 0 && timer.duration; // timer ran out
  const critical = running && remaining <= 5;

  const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
  const ss = String(remaining % 60).padStart(2, "0");

  return (
    <div className={`mb-4 border ${critical ? "border-red-600 animate-pulse" : expired ? "border-red-800" : active ? "border-gold/60" : "border-metal"} bg-black/40 p-2.5`} data-testid="interrogation-timer-bar">
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-2">
          <Clock size={12} className={critical ? "text-red-400" : "text-gold"} />
          <span className="text-[9px] tracking-[0.35em] font-mono-ar text-gold">INTERROGATION · TIMER</span>
          {expired && <span className="text-[9px] font-mono-ar text-red-400 border border-red-800 px-1.5 py-0.5">EXPIRED</span>}
        </div>
        <div className={`font-mono-ar tabular-nums text-2xl ${critical ? "text-red-400" : running ? "text-gold" : "text-zinc-300"}`} data-testid="interrogation-timer-clock">
          {mm}:{ss}
        </div>
      </div>
      {/* Progress bar */}
      <div className="h-1.5 bg-black/60 border border-metal/50 relative overflow-hidden mb-2">
        <div className={`absolute inset-y-0 right-0 transition-all duration-200 ${critical ? "bg-gradient-to-l from-red-600 to-red-400" : "bg-gradient-to-l from-yellow-700 to-yellow-500"}`}
             style={{ width: `${pct}%` }} data-testid="interrogation-timer-progress" />
      </div>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1">
          {!running ? (
            <button onClick={() => call(active && remaining > 0 && remaining < (timer?.duration || duration) ? "resume" : "start")}
              data-testid="interrogation-timer-start"
              className="text-[11px] font-cairo px-3 py-1 border border-gold text-gold hover:bg-[rgba(197,160,89,0.15)] flex items-center gap-1">
              <Play size={11} /> {active && remaining > 0 && remaining < (timer?.duration || duration) ? "استئناف" : "بدء"}
            </button>
          ) : (
            <button onClick={() => call("pause")} data-testid="interrogation-timer-pause"
              className="text-[11px] font-cairo px-3 py-1 border border-amber-600 text-amber-400 hover:bg-amber-950/30 flex items-center gap-1">
              <Pause size={11} /> إيقاف مؤقت
            </button>
          )}
          <button onClick={() => call("reset")} data-testid="interrogation-timer-reset"
            className="text-[11px] font-cairo px-2.5 py-1 border border-metal text-zinc-300 hover:border-gold hover:text-gold flex items-center gap-1">
            <RotateCcw size={11} /> إعادة
          </button>
          <button onClick={() => call("skip")} data-testid="interrogation-timer-skip"
            className="text-[11px] font-cairo px-2.5 py-1 border border-red-900/60 text-red-400 hover:bg-red-950/30 flex items-center gap-1">
            <SkipForward size={11} /> تجاوز
          </button>
        </div>
        <div className="flex items-center gap-1" data-testid="interrogation-timer-duration">
          <span className="text-[9px] text-zinc-500 font-mono-ar">مدة السؤال:</span>
          {[15, 30, 60, 90].map(d => (
            <button key={d} onClick={() => setDuration(d)} disabled={running}
              data-testid={`interrogation-timer-duration-${d}`}
              className={`text-[10px] font-mono-ar w-9 py-0.5 border ${duration === d ? "border-gold text-gold bg-[rgba(197,160,89,0.15)]" : "border-metal text-zinc-400 hover:border-gold/50"} ${running ? "opacity-50 cursor-not-allowed" : ""}`}>
              {d}s
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============ Voice Fingerprint Panel (host-only) ============
// Compact metadata about the suspect's TTS voice profile. Helps the host manage voices
// and adds a forensic-audio flavor to the case. Never spoils guilt — pure UX metadata.
function VoiceFingerprintPanel({ suspect }) {
  const [expanded, setExpanded] = useState(false);
  const [fp, setFp] = useState(null);
  useEffect(() => {
    if (!suspect) { setFp(null); return; }
    // Compute after voices are ready (Chrome loads them async)
    const compute = () => setFp(TTS.voiceFingerprintFor(suspect));
    compute();
    const t = setTimeout(compute, 400);
    return () => clearTimeout(t);
  }, [suspect?.id]);
  if (!fp) return null;
  return (
    <div className="mt-3 border border-metal bg-black/30" data-testid="voice-fingerprint-panel">
      <button onClick={() => setExpanded(x => !x)} data-testid="voice-fingerprint-toggle"
        className="w-full flex items-center justify-between px-2.5 py-1.5 text-[10px] font-mono-ar text-gold hover:bg-[rgba(197,160,89,0.06)]">
        <span className="flex items-center gap-1.5"><Waves size={11} /> بصمة الصوت · VOICE-ID</span>
        <span className="text-zinc-500 text-[9px]">{expanded ? "طيّ" : "توسيع"}</span>
      </button>
      {expanded && (
        <div className="px-2.5 pb-2 text-[11px] space-y-0.5 text-zinc-300" data-testid="voice-fingerprint-details">
          <FpRow k="النوع"   v={fp.gender} />
          <FpRow k="النغمة"  v={fp.tone} />
          <FpRow k="الخامة"  v={fp.timbre} />
          <FpRow k="السرعة"  v={fp.speed} />
          <FpRow k="الإيقاع" v={fp.cadence} />
          <div className="grid grid-cols-2 gap-1 pt-1 border-t border-metal mt-1 text-[10px] font-mono-ar text-zinc-500">
            <div>pitch: <span className="text-gold">{fp.pitch.toFixed(2)}</span></div>
            <div>rate: <span className="text-gold">{fp.rate.toFixed(2)}</span></div>
            <div className="col-span-2 truncate" title={fp.voice_name}>voice: <span className="text-zinc-300">{fp.voice_name}</span></div>
            <div>lang: <span className="text-zinc-300">{fp.voice_lang}</span></div>
            <div>seed: <span className="text-zinc-300">{fp.seed}</span></div>
          </div>
          <div className="text-[9px] text-zinc-600 leading-tight pt-1">
            ملف صوت المشتبه به مُولَّد حتمياً — نفس البصمة في كل جلسة (استمرارية للشخصية).
          </div>
        </div>
      )}
    </div>
  );
}
// ============ VOICE NOT READY chip ============
// Small host-facing indicator shown next to interrogation voice controls when
// the real interrogation audio file for that suspect/question/kind is missing.
// The moment the correctly-named file is dropped into
// /app/backend/media/<case_id>/interrogation/<SUSPECT_ID>_q<N>_<q|a>.mp3
// the chip disappears automatically (30s poll).
function VoiceNotReadyChip({ caseId, suspectId, qIndex, kind }) {
  const [status, setStatus] = useState(null); // 'ready' | 'missing' | null
  useEffect(() => {
    let stop = false;
    if (!caseId || !suspectId || qIndex == null) return;
    const itemId = `${suspectId}_q${qIndex}_${kind}`;
    const check = () => {
      api.get(`/media-lookup/${caseId}/interrogation/${itemId}`)
        .then(({ data }) => { if (!stop) setStatus(data?.status || "missing"); })
        .catch(() => { if (!stop) setStatus("missing"); });
    };
    check();
    const iv = setInterval(check, 30000);
    return () => { stop = true; clearInterval(iv); };
  }, [caseId, suspectId, qIndex, kind]);
  if (status !== "missing") return null;
  return (
    <span
      dir="rtl"
      data-testid={`voice-not-ready-${kind}-${qIndex}`}
      title="صوت الاستجواب الحقيقي غير متوفر — يُستخدم صوت المُركّب مؤقتاً"
      className="inline-flex items-center gap-1 text-[9px] font-mono-ar tracking-[0.25em] text-[#c5a059] border border-[#c5a059]/50 bg-black/60 px-1.5 py-0.5"
    >
      <MicOff size={9} strokeWidth={1.4} />
      <span className="font-cairo tracking-normal text-[10px] text-zinc-300">صوت الاستجواب غير متوفر</span>
      <span className="opacity-70">· VOICE NOT READY</span>
    </span>
  );
}

function FpRow({ k, v }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-zinc-500 font-mono-ar text-[10px]">{k}</span>
      <span className="text-white font-cairo">{v}</span>
    </div>
  );
}



